// features/premium/checkoutFlow.ts — the one funnel shared by /premium (Pro
// upgrade) and /support ("Become a supporter").
//
// Fix-round F1: there is NO Payment-Link fallback here, deliberately. The
// legacy static link (VITE_SUPPORT_URL / getSupportUrl) charges $5.00 flat
// and carries no client_reference_id, so a payment made through it can never
// be attributed to an account — the payer would be charged after being shown
// "$4.99" and would be granted nothing, permanently. Two hard rules, both
// pinned by checkoutFlow.test.ts:
//
//   1. Never send a user to a payment URL whose price disagrees with the one
//      displayed to them.
//   2. Never send a user to a payment URL that cannot be attributed to their
//      account.
//
// So when Checkout is unconfigured server-side (STRIPE_* env unset — prod's
// state today) this reports 'unavailable' and the CALLER renders the disabled
// "Supporter link coming soon" affordance. Do not re-add a fallback.

import { billingService } from '@/core/services/billingService';

export type CheckoutOutcome =
  | { kind: 'redirected' }
  /** Billing is not configured server-side. No payment URL exists — callers
   * must render the disabled affordance, never navigate anywhere. */
  | { kind: 'unavailable' }
  /** The server refused because this account ALREADY holds Pro (409). Not an
   * error and not retryable: nothing was charged, and no number of retries can
   * make it succeed. Callers must say the member already has Pro and re-read
   * billing state — a stale client-side tier is what put the upgrade control
   * on screen in the first place. Deliberately NOT folded into 'unavailable':
   * that one renders the disabled "coming soon" affordance, which would be a
   * lie here (checkout is configured and working fine). */
  | { kind: 'already_subscribed' }
  | { kind: 'error' };

/**
 * Starts real Stripe Checkout; on success, navigates the current tab to the
 * Stripe-hosted URL (returns before that navigation completes — callers don't
 * need to await anything further).
 *
 * Reports 'unavailable' ONLY when billing is unconfigured server-side,
 * 'already_subscribed' when the caller is already Pro, and 'error' on any
 * other failure — including a 200 that somehow carried no usable URL. None of
 * those three navigate.
 */
export async function startCheckout(): Promise<CheckoutOutcome> {
  try {
    const start = await billingService.checkout();
    switch (start.kind) {
      case 'unconfigured':
        return { kind: 'unavailable' };
      case 'already_subscribed':
        return { kind: 'already_subscribed' };
      case 'url':
        // Rule 2 above: never navigate to nothing — '' would reload the
        // current page and look like a dead button. But this is an 'error',
        // NOT 'unavailable': the server answered 200, so Checkout plainly IS
        // configured, and 'unavailable' would latch the caller's disabled
        // "Supporter link coming soon" affordance — telling a member the
        // thing does not exist when it exists and simply failed once.
        if (!start.url) return { kind: 'error' };
        window.location.href = start.url;
        return { kind: 'redirected' };
      default: {
        // Exhaustiveness guard. Every CheckoutStart member is mapped above by
        // NAME; a new one added later must be mapped deliberately, so this
        // makes it a COMPILE error instead of letting it fall through to
        // "checkout isn't set up" — which is what the removed fall-through
        // did to a working-but-empty 200.
        const unhandled: never = start;
        void unhandled;
        return { kind: 'error' };
      }
    }
  } catch {
    return { kind: 'error' };
  }
}
